import { useEffect, useRef } from 'react';
import { loadSessionJson, saveSessionJson } from '@/lib/listViewPersistence';

/**
 * Writes `state` to in-memory store whenever it changes (after optional skip of first frame).
 * Cleared on full page reload.
 */
export function usePersistedListView<T extends Record<string, unknown>>(
  storageKey: string | undefined,
  state: T,
  options?: { enabled?: boolean },
): void {
  const enabled = options?.enabled !== false && !!storageKey;
  const skipFirstWrite = useRef(true);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    if (!enabled || !storageKey) return;
    if (skipFirstWrite.current) {
      skipFirstWrite.current = false;
    }
    saveSessionJson(storageKey, state);
  }, [enabled, storageKey, state]);

  // Flush latest slice when leaving the list (e.g. open application detail).
  useEffect(() => {
    if (!enabled || !storageKey) return;
    return () => {
      saveSessionJson(storageKey, stateRef.current);
    };
  }, [enabled, storageKey]);
}

/**
 * Avoid resetting page to 1 when hydrating filters/sort from session storage.
 */
export function useSkipInitialEffect(effect: () => void, deps: unknown[]): void {
  const hydrated = useRef(false);
  useEffect(() => {
    if (!hydrated.current) {
      hydrated.current = true;
      return;
    }
    effect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export { loadSessionJson };
